import { formatNumber } from '@/shared/lib';

/**
 * Credits zijn hele eenheden, geen euro's: `25 credits`, `1 credit`. Met
 * `signed` krijgt een boeking haar teken (`+25 credits`, `−3 credits`).
 */
export function formatCredits(value: number, options: { signed?: boolean } = {}): string {
    const size = Math.abs(value);
    const sign = value < 0 ? '−' : options.signed && value > 0 ? '+' : '';

    return `${sign}${formatNumber(size)} ${size === 1 ? 'credit' : 'credits'}`;
}

/**
 * Nederlandse teksten van het platformbeheer. Jij-vorm, labels zijn zelfstandige
 * naamwoorden, knoppen werkwoorden.
 */
export const adminCopy = {
    eyebrowPlatform: 'Platform',
    tenantCount: (n: number) => `${n} ${n === 1 ? 'keuringsbedrijf' : 'keuringsbedrijven'}`,
    userCount: (n: number) => `${n} ${n === 1 ? 'persoon' : 'personen'}`,

    /* Overzicht */
    overviewTitle: 'Platformbeheer',
    overviewPeriod: 'laatste 30 dagen',
    metricsTitle: 'Cijfers',
    metricOrders: 'Nieuwe keuringen',
    metricLeadTime: 'Gem. doorlooptijd rapport',
    metricLeadTimeUnit: (minutes: number) => `u ${minutes}m`,
    metricReports: (n: number) => `${n} ${n === 1 ? 'rapport' : 'rapporten'} verstuurd`,
    metricCredits: 'Verbruikte credits',
    metricActiveUsers: 'Actieve gebruikers',
    metricUnknown: 'onbekend',
    metricsToTenants: 'Per bedrijf bekijken',
    metricsToUsers: 'Wie werkt waar',
    metricsIncomplete: 'Nog niet elke bron levert',
    metricsEmpty: 'Nog geen momentopnames',

    /* Organisaties */
    tenantsTitle: 'Keuringsbedrijven',
    tenantsSearch: 'Zoeken',
    tenantsSearchPlaceholder: 'Naam of korte naam',
    tenantsStatus: 'Status',
    tenantsStatusAll: 'Alle statussen',
    tenantsTableLabel: 'Keuringsbedrijven',
    columnName: 'Naam',
    columnPlan: 'Abonnement',
    columnStatus: 'Status',
    columnContact: 'Contact',
    columnSince: 'Klant sinds',
    tenantsEmpty: 'Geen keuringsbedrijven die aan dit filter voldoen.',
    tenantsNoun: 'keuringsbedrijven',
    noPlan: 'Geen abonnement',

    /* Eén organisatie */
    tenantBack: 'Alle keuringsbedrijven',
    tenantEyebrow: (slug: string) => `keuringsbedrijf · ${slug}`,
    tenantLoading: 'Keuringsbedrijf laden',
    tenantError: 'Dit keuringsbedrijf kon niet geladen worden',
    tenantEmail: 'E-mail',
    tenantPhone: 'Telefoon',
    tenantTimezone: 'Tijdzone',
    tenantSince: 'Klant sinds',
    tenantTrialEnds: 'Proefperiode tot',
    tenantSuspendedOn: (date: string | null, reason: string) =>
        date ? `Opgeschort op ${date}: ${reason}` : `Opgeschort: ${reason}`,
    planLabel: 'Abonnement',
    planLoading: 'Abonnementen laden…',
    planSaved: (name: string) => `Abonnement staat nu op ${name}.`,
    planOptionHint: (monthlyPrice: string, inspections: number) =>
        `${monthlyPrice} per maand · ${inspections} ${inspections === 1 ? 'keuring' : 'keuringen'}`,
    suspend: 'Opschorten',
    resume: 'Hervatten',
    suspendConfirmLabel: (name: string) => `Bevestig opschorten van ${name}`,
    suspendConfirm: (name: string) =>
        `${name} wordt opgeschort. Niemand van dit bedrijf kan dan nog inloggen, lopende opnames kunnen niet synchroniseren en er worden geen rapporten meer verstuurd. Gegevens blijven staan en hervatten kan direct.`,
    suspendYes: 'Ja, bedrijf opschorten',
    cancel: 'Annuleren',
    reason: 'Reden',
    reasonTooShort: (min: number) => `Geef een reden van minimaal ${min} tekens.`,
    suspendPlaceholder: 'Bijvoorbeeld: betalingsachterstand van drie maanden',

    membersTitle: 'Mensen bij dit bedrijf',
    membersEmpty: 'Nog niemand werkt bij dit bedrijf.',
    membersLabel: (name: string) => `Mensen bij ${name}`,
    memberSince: (date: string) => `sinds ${date}`,

    /* Tegoedregels */
    creditRulesTitle: 'Tegoedregels',
    creditRulesIntro: (tenant: string) =>
        `Of nieuwe keuringen van ${tenant} stoppen als de keuringen op zijn, en hoe ver het saldo onder nul mag. Alleen het platform zet dit; de eigenaar ziet het als uitleg. Met blokkeren uit of een marge maakt het bedrijf keuringen die het niet betaalde.`,
    creditRulesBlock: 'Nieuwe keuringen stoppen als de keuringen op zijn',
    creditRulesBlockOn: 'Aan: na een aankoop kan het bedrijf weer nieuwe keuringen maken.',
    creditRulesBlockOff:
        'Uit: het bedrijf kan zonder keuringen doorgaan en het saldo gaat onder nul. Er komt geen factuur achteraan.',
    creditRulesGrace: 'Ruimte onder nul',
    creditRulesGraceHint: (max: number) =>
        `Zoveel keuringen mag het saldo onder nul voordat nieuwe keuringen stoppen, zodat een lopende afspraak niet strandt. 0 tot ${max}; 0 is geen ruimte.`,
    creditRulesGraceInvalid: (max: number) => `Vul een heel aantal keuringen in, van 0 tot ${max}.`,
    creditRulesSave: 'Regels opslaan',
    creditRulesSaved: 'De tegoedregels zijn opgeslagen en gelden vanaf de volgende keuring.',

    /* Tegoed boeken */
    bookingTitle: 'Tegoed boeken',
    bookingIntro: (tenant: string) =>
        `Deze boeking komt met je naam en je reden in de audit log van ${tenant} en is niet terug te draaien. Corrigeren doe je met een tegenboeking.`,
    bookingDirection: 'Richting',
    bookingCredits: 'Credits',
    bookingCreditsHint: 'Hele credits, bijvoorbeeld 25',
    bookingCreditsInvalid: 'Vul een heel aantal credits groter dan nul in.',
    bookingReasonPlaceholder: 'Bijvoorbeeld: compensatie storing 3 september',
    bookingSubmit: 'Boeking vastleggen',
    bookingLoggedAs: (actor: string) => `Wordt gelogd als ${actor}`,
    bookingDone: (credits: string, balance: string) =>
        `Geboekt: ${credits}. Nieuw saldo: ${balance}.`,
    bookingReplayed: (balance: string) =>
        `Deze boeking stond er al; er is niets extra geboekt. Saldo: ${balance}.`,

    /* Meekijken */
    impersonateTitle: 'Meekijken als',
    impersonateIntro: (tenant: string) =>
        `Je kijkt mee bij ${tenant}. Alles wat je dan doet gebeurt op naam van die persoon en staat in de audit log. Tijdens de sessie staat er een balk bovenaan het scherm.`,
    impersonateTenant: 'Bedrijf',
    impersonateTenantHint:
        'Eerst het bedrijf, dan de persoon: iemand kan bij meer bedrijven werken.',
    impersonateUser: 'Gebruiker bij dit bedrijf',
    impersonateUserPick: 'Kies een gebruiker',
    impersonateUsersLoading: 'Gebruikers laden…',
    impersonateUserMissing: 'Kies een gebruiker.',
    impersonateMinutes: 'Duur in minuten',
    impersonateMinutesHint: 'Hoogstens 60 minuten.',
    impersonateReasonHint: 'Verplicht. Komt letterlijk in de audit log.',
    impersonateReasonPlaceholder: 'Bijvoorbeeld: melding 4412, rapport blijft hangen',
    impersonateStart: 'Meekijken starten',
    impersonateLogged: 'Sessie wordt gelogd',
    impersonateOptionHint: (role: string, status: string) => `${role}, ${status}`,

    sessionsTitle: 'Meekijksessies',
    sessionsEmpty: 'Bij dit bedrijf is nog niet meegekeken.',
    sessionActive: 'Loopt',
    sessionEnded: 'Beëindigd',
    sessionEnd: 'Beëindigen',
    sessionLine: (platformUser: string, target: string) => `${platformUser} als ${target}`,
    sessionUntil: (time: string) => `tot ${time}`,

    /* Balk */
    barText: (user: string, tenant: string) =>
        `Je kijkt mee als ${user} bij ${tenant}. Alles wat je hier doet gebeurt op hun naam en staat in de audit log.`,
    barStop: 'Stop meekijken',

    /* Gebruikers */
    usersTitle: 'Gebruikers over alle bedrijven',
    usersIntro: 'Eén rij per persoon. Open iemand om te zien bij welke bedrijven hij werkt.',
    usersTenant: 'Bedrijf',
    usersTenantAll: 'Alle bedrijven',
    usersRole: 'Rol',
    usersRoleAll: 'Alle rollen',
    usersStatus: 'Status',
    usersStatusAll: 'Alle statussen',
    usersSearch: 'Zoeken',
    usersSearchPlaceholder: 'Naam of e-mailadres',
    columnEmail: 'E-mailadres',
    columnCompanies: 'Bedrijven',
    columnLastLogin: 'Laatst ingelogd',
    usersEmpty: 'Geen gebruikers die aan dit filter voldoen.',
    usersNoun: 'personen',
    usersError: 'De gebruikers konden niet geladen worden',

    /* Eén persoon */
    userBack: 'Alle gebruikers',
    userEyebrow: 'persoon',
    userLoading: 'Persoon laden',
    userError: 'Deze persoon kon niet geladen worden',
    userContactTitle: 'Account',
    userPhone: 'Telefoon',
    userLastLogin: 'Laatst ingelogd',
    userSince: 'Account sinds',
    userPlatformAdmin: 'Platformbeheerder',
    userNeverLoggedIn: 'Nog nooit',
    userMembershipsTitle: (name: string) => `${name} werkt bij`,
    userMembershipsLabel: (name: string) => `Lidmaatschappen van ${name}`,
    userMembershipsEmpty: 'Deze persoon heeft geen lidmaatschappen.',
    userImpersonateAt: (tenant: string) => `Meekijken bij ${tenant}`,
    unknownRole: 'Onbekende rol',

    /* Audit log */
    auditTitle: 'Audit log',
    auditIntro: 'Elke handeling van een platformbeheerder, met actor en reden.',
    auditTenant: 'Bedrijf',
    auditTenantAll: 'Alle bedrijven',
    auditType: 'Type',
    auditTypeAll: 'Alle typen',
    auditFrom: 'Van',
    auditUntil: 'Tot',
    auditImpersonatedOnly: 'Alleen tijdens meekijken',
    columnWhen: 'Wanneer',
    columnType: 'Type',
    columnTenant: 'Bedrijf',
    columnWhat: 'Wat er gebeurde',
    columnBy: 'Door',
    auditPlatform: 'Platform',
    auditSystem: 'Systeem',
    auditReason: (reason: string) => `Reden: ${reason}`,
    auditDuringImpersonation: 'tijdens meekijken',
    auditEmpty: 'Geen logregels die aan dit filter voldoen.',
    auditNoun: 'logregels',

    /* Audit log als eigen scherm */
    auditPageTitle: 'Audit log',
    auditPageEyebrow: 'alle handelingen van het platform',

    /* Een keuringsbedrijf aanmaken */
    createAction: 'Keuringsbedrijf aanmaken',
    createTitle: 'Keuringsbedrijf aanmaken',
    createEyebrow: 'nieuw keuringsbedrijf',
    createBack: 'Alle keuringsbedrijven',
    createIntro:
        'Het bedrijf start in een proefperiode van 30 dagen. De eigenaar krijgt een uitnodiging per mail; heeft het adres al een Smartkeur-account, dan komt het bedrijf er meteen bij.',
    createCompany: 'Bedrijf',
    createName: 'Bedrijfsnaam',
    createSlug: 'Korte naam',
    createSlugHint: 'Kleine letters, cijfers en koppeltekens. Wordt gebruikt in adressen.',
    createSlugInvalid: 'Gebruik alleen kleine letters, cijfers en losse koppeltekens.',
    createEmail: 'E-mail van het bedrijf',
    createPhone: 'Telefoon van het bedrijf',
    createOwner: 'Eerste eigenaar',
    createOwnerName: 'Naam',
    createOwnerEmail: 'E-mailadres',
    createOwnerPhone: 'Telefoon',
    createCredits: 'Startsaldo',
    createCreditsHint: 'Hele credits. Komt als eerste boeking in het grootboek.',
    createCreditsInvalid: 'Vul een heel aantal credits in, 0 of meer.',
    createTemplates: 'Vragenlijsten',
    createTemplatesHint: 'Het bedrijf krijgt een eigen kopie die het later zelf kan aanpassen.',
    createTemplatesLoading: 'Vragenlijsten laden…',
    createRequired: 'Vul dit veld in.',
    createSubmit: 'Keuringsbedrijf aanmaken',
    createBusy: 'Bezig met aanmaken…',
    createdTitle: (name: string) => `${name} is aangemaakt`,
    createdTrial: 'Het bedrijf staat in de proefperiode.',
    createdInvited: (email: string) =>
        `${email} krijgt een uitnodiging om een wachtwoord te kiezen en is daarna eigenaar.`,
    createdExisting: (name: string, email: string) =>
        `${name} (${email}) had al een Smartkeur-account en is nu eigenaar. Er gaat geen uitnodiging uit.`,
    createdCredits: (credits: string) => `Startsaldo: ${credits}.`,
    createdTemplates: (names: string) => `Vragenlijsten: ${names}.`,
    createdNoTemplates: 'Nog geen vragenlijsten; die kun je later uitrollen.',
    createdOpen: 'Keuringsbedrijf openen',
    createdAnother: 'Nog een aanmaken',

    /* Vragenlijsten uitrollen */
    integrationsTitle: 'Koppelingen',
    integrationsEyebrow: 'beheer-MCP',
    integrationsIntro:
        "Met een beheersleutel beheert je AI-agent de keuringsbedrijven via de beheer-MCP: zoeken, status, tegoed, abonnementen, adressen, uitnodigingen en het auditlog. Alleen platformbeheerders maken zo'n sleutel.",

    templatesTitle: 'Vragenlijsten',
    templatesEyebrow: 'vragenlijsten van het platform',
    templatesIntro:
        'Uitrollen geeft een bedrijf een eigen, gepubliceerde kopie. Heeft een bedrijf een oudere versie, dan komt deze versie erbij als nieuwe versie van zijn eigen vragenlijst. Is een bedrijf al bij, dan wordt het overgeslagen.',
    templatesEmpty: 'Het platform levert nog geen vragenlijsten.',
    templatesError: 'De vragenlijsten konden niet geladen worden',
    templateVersion: (version: number) => `versie ${version}`,
    templatePublished: (date: string) => `gepubliceerd op ${date}`,
    rollout: 'Uitrollen',
    rolloutTitle: (name: string) => `${name} uitrollen`,
    rolloutPick: (max: number) => `Kies de bedrijven, hoogstens ${max} tegelijk.`,
    rolloutAll: 'Alle bedrijven kiezen',
    rolloutNone: 'Niets kiezen',
    rolloutTooMany: (max: number) => `Kies hoogstens ${max} bedrijven tegelijk.`,
    rolloutSubmit: (count: number) =>
        `Uitrollen naar ${count} ${count === 1 ? 'bedrijf' : 'bedrijven'}`,
    rolloutBusy: 'Bezig met uitrollen…',
    rolloutResult: 'Resultaat',
    rolloutTenantsLabel: (name: string) => `Bedrijven voor ${name}`,

    unknown: '—',
    retry: 'Opnieuw proberen',
    loading: (what: string) => `${what} laden`,
} as const;
